import { useState } from 'react'
import type { Pools } from '../../event/types'
import type { AdminEvent } from './useAdminEvent'

const button =
  'cursor-pointer rounded-md border border-[var(--page-rule)] px-3.5 py-2 text-sm font-semibold hover:bg-[var(--page-canvas-sunken)] disabled:cursor-default disabled:opacity-50'

const FIELDS: { key: keyof Pools; label: string; help: string }[] = [
  { key: 'agencies', label: 'Missions', help: 'Federal agencies. Not repeated while unused ones remain.' },
  { key: 'domains', label: 'Domains', help: 'May repeat across teams.' },
  { key: 'technologies', label: 'Technologies', help: 'May repeat across teams.' },
]

const toText = (pools: Pools) => ({
  agencies: pools.agencies.join('\n'),
  domains: pools.domains.join('\n'),
  technologies: pools.technologies.join('\n'),
})

/**
 * The three lists the slot machine draws from, one entry per line. Edits stay
 * local until saved, and follow the saved document whenever it changes.
 */
export default function PoolsEditor({ admin }: { admin: AdminEvent }) {
  const { state, busy, act } = admin
  const [text, setText] = useState(() => toText(state?.pools ?? { agencies: [], domains: [], technologies: [] }))
  const [synced, setSynced] = useState(state?.revision)

  if (state && synced !== state.revision) {
    setSynced(state.revision)
    setText(toText(state.pools))
  }
  if (!state) return null

  const lines = (value: string) => value.split('\n').map((v) => v.trim()).filter(Boolean)

  return (
    <div>
      <div className="grid gap-4 md:grid-cols-3">
        {FIELDS.map((field) => (
          <label key={field.key} className="flex flex-col gap-1 text-sm font-semibold">
            {field.label} ({lines(text[field.key]).length})
            <span className="font-normal text-[var(--page-ink-muted)]">One per line. {field.help}</span>
            <textarea
              rows={10}
              value={text[field.key]}
              onChange={(e) => setText({ ...text, [field.key]: e.target.value })}
              className="rounded-md border border-[var(--page-rule)] bg-[var(--page-canvas)] px-2.5 py-1.5 font-normal"
            />
          </label>
        ))}
      </div>
      <button
        type="button"
        className={`${button} mt-3`}
        disabled={busy}
        onClick={() =>
          act(
            { type: 'savePools', pools: { agencies: lines(text.agencies), domains: lines(text.domains), technologies: lines(text.technologies) } },
            'Lists saved.',
          )
        }
      >
        Save lists
      </button>
    </div>
  )
}
